/* eslint-disable @reatom/unit-naming-rule */
import { action } from '@reatom/framework'

import { WeakRegistry } from '@blade/std-lib'
import { currencyRegistry } from '@root/entities/currency'
import { Network } from '@root/entities/network'
import {
  premiumMetaRegistry,
  premiumMetaUpdateReceived,
} from '@root/entities/premium'
import {
  pairPartialUpdateEvent,
  pairUpdateEvent,
  pairUpdateMigrationEvent,
} from '@root/entities/realtime'
import { newTxEvent } from '@root/entities/realtime/atoms/new-tx-event'
import { holderChartUpdateEvent } from '@root/features/realtime/reatom-websocket-main'
import { environmentAtom } from '@root/shared'
import { createRecentCache } from '@root/shared/lib/recent-cache'

import { holderDelta } from '../model/holder-delta'
import { PairAddress } from '../model/PairAddress'

import { reatomPair, type PairModel } from './reatom-pair'

import type { tradeApi } from '@blade/socket-types'
import type { Ctx } from '@reatom/framework'
import type { Trade } from '@root/entities/trade'
import type { PoolHolderChartsUpdate } from '@root/features/realtime/model/poolholder-chart-update-data'
import type { ServicesApi } from '@root/shared/api'

const mkKey = (pairAddress: PairAddress, network: Network.Network) => {
  return `pair:${network.toString()}:${pairAddress.toString()}`
}

export class PairRegistry {
  private registry = new WeakRegistry<string, PairModel>()

  /**
   * The last few pairs, held strongly.
   *
   * Weak references are right for a feed: five hundred cards are resolved to
   * show thirty, and when the list lets go the models should go with it. They
   * are wrong for the pair on screen, because nothing else holds that one
   * either - a resource keeps it in its state only while it is connected -
   * so between one component asking for it and the next it could be
   * collected, and the next one would fetch it again.
   */
  private recent = createRecentCache<PairModel>({ size: 8 })

  public get = (pairAddress: PairAddress, network: Network.Network) => {
    const key = mkKey(pairAddress, network)
    return this.recent.get(key) ?? this.registry.get(key)
  }

  public getOrCreate = (
    pairAddress: PairAddress,
    network: Network.Network,
    factory: () => PairModel,
  ) => {
    const key = mkKey(pairAddress, network)
    const existing = this.get(pairAddress, network)
    if (existing) {
      return existing
    }
    const created = this.registry.getOrCreate(key, factory)
    this.recent.set(key, created)
    return created
  }

  public set = (model: PairModel) => {
    const key = mkKey(model.address, model.network)
    this.registry.set(key, model)
    this.recent.set(key, model)
  }

  public resolveFromDto = (ctx: Ctx, dto: ServicesApi.PairDto) => {
    const existing = this.get(
      PairAddress(dto.poolAddress),
      Network.fromDto(dto.network),
    )
    if (existing) {
      existing.updateFromDto(ctx, dto)
      return existing
    }

    const {
      assets: { tokenLogosHost },
    } = ctx.get(environmentAtom)

    const quote = currencyRegistry.resolveFromTokenMeta(ctx, dto.quote)

    const premiumMeta = premiumMetaRegistry.resolve(ctx, {
      dto: dto.premiumInfo,
      currency: quote,
    })

    const pair = reatomPair(dto, tokenLogosHost, premiumMeta)

    this.set(pair)

    return pair
  }

  static init() {
    pairUpdateEvent.onCall((ctx, dto) => {
      pairDtoUpdateReceivedEvent(ctx, dto)
    })
    newTxEvent.onCall((ctx, data) => {
      pairUpdateFromTrade(ctx, data)
    })
    holderChartUpdateEvent.onCall((ctx, data) => {
      updatePairFromHoldersEvents(ctx, data)
    })
    pairPartialUpdateEvent.onCall((ctx, dto) => {
      updatePairFromPairUpdateEvent(ctx, dto)
    })
    pairUpdateMigrationEvent.onCall((ctx, dto) => {
      const address = PairAddress(dto.address)
      const network = Network.fromDto(dto.network)
      const existing = pairRegistry.get(address, network)
      if (existing) {
        const migratedPair = pairRegistry.resolveFromDto(ctx, dto.migratedPair)
        existing.migratedPair(ctx, migratedPair)
      }
    })
  }
}

// TODO remove top-level side effects
export const pairRegistry = new PairRegistry()

// TODO remove top-level side effects
setTimeout(() => {
  PairRegistry.init()
}, 0)

export const pairDtoUpdateReceivedEvent = action(
  (ctx, dto: ServicesApi.PairDto) => {
    premiumMetaUpdateReceived(ctx, {
      currency: dto.quote,
      premiumMeta: dto.premiumInfo,
    })

    const model = pairRegistry.get(
      PairAddress(dto.poolAddress),
      Network.fromDto(dto.network),
    )
    if (model) {
      model.updateFromDto(ctx, dto)
    }
  },
  'pairDtoUpdateReceivedEvent',
)

const pairUpdateFromTrade = action((ctx, data: Trade.Trade) => {
  const model = pairRegistry.get(data.pairAddress, data.network)

  if (model) {
    model.athPrice.set(ctx, data.tokenPriceInUsd)
    model.athMarketCap.set(ctx, data.marketCapUsd)
    const pairUpdatedAt = ctx.get(model.updatedAt)
    if (data.timestamp > pairUpdatedAt) {
      model.marketCapUsd.set(ctx, data.marketCapUsd)
      model.liquidityUsd.set(ctx, data.liquidityInUsd)
      if (data.type === 'liquidityRemove') {
        model.hasLiquidityRemovedTx(ctx, true)
      }
    }

    // Count the trade into the windows too, so a card's transactions and
    // volume move with the market rather than with the server's rebuild of
    // the whole card. The next rebuilt card overwrites these.
    if (data.type === 'buy' || data.type === 'sell') {
      model.applyTradeToStats(ctx, {
        isBuy: data.type === 'buy',
        volumeUsd: data.totalUsd,
        priceUsd: data.tokenPriceInUsd,
        at: data.timestamp.getTime(),
      })

      // The holder count comes from a job that recounts balances about once
      // every three minutes, so it is the one figure a three-second rebuild
      // cannot make move. The trade says whether this wallet arrived from
      // nothing or left with nothing, which is the whole of it.
      const delta = holderDelta(
        data.maker.balanceCurrencyBefore,
        data.maker.balanceCurrency,
      )
      if (delta !== 0) {
        model.holdersCount(
          ctx,
          Math.max(0, ctx.get(model.holdersCount) + delta),
        )
      }
    }
  }
}, 'pairUpdateFromTrade')

const updatePairFromPairUpdateEvent = action(
  (ctx, dto: tradeApi.PairPartialUpdateData) => {
    const model = pairRegistry.get(
      PairAddress(dto.address),
      Network.fromDto(dto.network),
    )
    if (!model) {
      return
    }
    model.updateFromPartial(ctx, dto)
  },
)

const updatePairFromHoldersEvents = action(
  (ctx, data: PoolHolderChartsUpdate) => {
    const model = pairRegistry.get(data.pairAddress, data.network)
    if (!model) {
      return
    }
    const pairUpdatedAt = ctx.get(model.updatedAt)

    if (data.holdersCount && data.holdersCount.timestamp > pairUpdatedAt) {
      model.holdersCount(ctx, data.holdersCount.value.toNumber())
    }

    if (
      data.top10HoldersPercentage &&
      data.top10HoldersPercentage.timestamp > pairUpdatedAt
    ) {
      model.top10holding.set(ctx, data.top10HoldersPercentage.value)
    }

    if (
      data.insidersHolding &&
      data.insidersHolding.timestamp > pairUpdatedAt
    ) {
      model.updateInsiders(ctx, {
        holding: data.insidersHolding.value,
      })
    }
  },
)
